import React, { useState, useEffect } from 'react';
import { apiCall } from '../config';
import './ProductProfitAnalysis.css';

const ProductProfitAnalysis = () => {
  const [channel, setChannel] = useState('Amazon');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [profitData, setProfitData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Set default dates (last 30 days)
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  }, []);

  const handleAnalyze = async () => {

    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Making API call to /api/profit-analysis-enhanced');
      console.log('Request data:', { channel, startDate, endDate });
      
      const response = await apiCall('/api/profit-analysis-enhanced', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channel,
          startDate,
          endDate
        })
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('Profit analysis data:', data);
        console.log('Data length:', data.length);
        console.log('First product:', data[0]);
        setProfitData(data);
      } else {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        setError(errorData.error || 'Failed to fetch profit analysis data');
      }
    } catch (err) {
      console.error('Network error:', err);
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Get all unique BOM categories from the profit data
  const getAllBOMCategories = () => {
    const categories = new Set();
    console.log('Calculating BOM categories from profit data:', profitData);
    profitData.forEach(product => {
      console.log('Processing product:', product.IGD_Name);
      console.log('Cost of goods:', product.cost_of_goods);
      if (product.cost_of_goods && product.cost_of_goods.categories) {
        console.log('Categories found:', Object.keys(product.cost_of_goods.categories));
        Object.keys(product.cost_of_goods.categories).forEach(category => {
          categories.add(category);
        });
      }
    });
    const result = Array.from(categories).sort();
    console.log('Final BOM categories:', result);
    return result;
  };

  const bomCategories = getAllBOMCategories();
  console.log('BOM categories for render:', bomCategories);
  console.log('Profit data length for render:', profitData.length);

  return (
    <div className="profit-analysis-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Product Profit Analysis Dashboard</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Analyze profit and loss across sales channels</li>
              <li>Compare product performance and margins</li>
              <li>Track cost breakdowns and contribution margins</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        <div className="profit-analysis-header">
          <h1>Product Profit Analysis</h1>
          <p>Analyze profit and loss for products across different sales channels</p>
        </div>

        <div className="profit-analysis-controls">
        <div className="control-group">
          <label htmlFor="channel">Channel:</label>
          <select
            id="channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
          >
            <option value="Retail">Retail</option>
            <option value="Amazon">Amazon</option>
            <option value="Shopify">Shopify</option>
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="startDate">Start Date:</label>
          <input
            type="date"
            id="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="control-group">
          <label htmlFor="endDate">End Date:</label>
          <input
            type="date"
            id="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <button 
          className="analyze-button"
          onClick={handleAnalyze}
          disabled={loading}
        >
          {loading ? 'Analyzing...' : 'Analyze'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {profitData.length > 0 && (
        <div className="profit-analysis-results">
          <div className="table-container">
            <table className="profit-table">
              <thead>
                <tr>
                  <th>Product</th>
                  {profitData.map((product, index) => (
                    <th key={index}>
                                              <div className="product-header">
                          <div className="product-name">{product.IGD_Name}</div>
                          <div className="product-asin">
                            {channel === 'Shopify' ? 'Shopify SKU: ' : 
                             channel === 'Retail' ? 'IGD SKU: ' : 'ASIN: '}{product.ASIN}
                          </div>
                        </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="row-label">Average Gross Sales Price</td>
                  {profitData.map((product, index) => (
                    <td key={index} className="currency-cell">
                      {formatCurrency(product.average_gross_sales_price)}
                    </td>
                  ))}
                </tr>
                {channel !== 'Retail' && (
                  <tr>
                    <td className="row-label">Promotional Discounts</td>
                    {profitData.map((product, index) => (
                      <td key={index} className="currency-cell negative">
                        {formatCurrency(product.promotional_discounts)}
                      </td>
                    ))}
                  </tr>
                )}
                {channel !== 'Retail' && (
                  <tr>
                    <td className="row-label">{channel === 'Shopify' ? 'Processing Fees' : 'Amazon Selling Fees'}</td>
                    {profitData.map((product, index) => (
                      <td key={index} className="currency-cell negative">
                        {formatCurrency(channel === 'Shopify' ? product.processing_fees : product.amazon_selling_fees)}
                      </td>
                    ))}
                  </tr>
                )}
                {channel !== 'Retail' && (
                  <tr>
                    <td className="row-label">{channel === 'Shopify' ? 'FBA Fees' : 'Amazon FBA Fees'}</td>
                    {profitData.map((product, index) => (
                      <td key={index} className="currency-cell negative">
                        {formatCurrency(product.amazon_fba_fees)}
                      </td>
                    ))}
                  </tr>
                )}
                {channel !== 'Retail' && (
                  <tr>
                    <td className="row-label">Refunds</td>
                    {profitData.map((product, index) => (
                      <td key={index} className="currency-cell negative">
                        {formatCurrency(product.refunds)}
                      </td>
                    ))}
                  </tr>
                )}
                <tr className="total-row">
                  <td className="row-label">Total Payment Received</td>
                  {profitData.map((product, index) => (
                    <td key={index} className="currency-cell total">
                      {formatCurrency(product.total_net_price)}
                    </td>
                  ))}
                </tr>

                {/* Cost of Goods Sold Section */}
                {bomCategories.length > 0 && (
                  <>
                    {bomCategories.map(category => (
                      <tr key={category}>
                        <td className="row-label">Cost - {category}</td>
                        {profitData.map((product, index) => (
                          <td key={index} className="currency-cell negative">
                            {formatCurrency(product.cost_of_goods?.categories?.[category] || 0)}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td className="row-label">Total Cost of Goods</td>
                      {profitData.map((product, index) => (
                        <td key={index} className="currency-cell total negative">
                          {formatCurrency(product.cost_of_goods?.total_cost || 0)}
                        </td>
                      ))}
                    </tr>
                  </>
                )}

                {/* Variable Contribution Margin */}
                <tr className="total-row">
                  <td className="row-label">Variable Contribution Margin</td>
                  {profitData.map((product, index) => {
                    const grossProfit = (product.total_net_price || 0) - (product.cost_of_goods?.total_cost || 0);
                    const grossProfitPercentage = product.average_gross_sales_price > 0 ? 
                      (grossProfit / product.average_gross_sales_price) * 100 : 0;
                    return (
                      <td key={index} className={`currency-cell total ${grossProfit >= 0 ? 'positive' : 'negative'}`}>
                        <div>{formatCurrency(grossProfit)}</div>
                        <div className="profit-percentage">
                          {grossProfitPercentage.toFixed(1)}%
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ProductProfitAnalysis; 